import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { GroupTypeBadge } from '@/components/ui/group-type-badge'
import { Header } from '@/components/layout/Header'
import { useGroups } from '@/hooks/useGroups'
import { usePageTitle } from '@/hooks/useDocumentHead'
import { useAuth } from '@/auth'
import { groupService } from '@/services/groupService'
import { Trophy, Users, Plus, ChevronRight, AlertCircle, Shield, Medal, Loader2 } from 'lucide-react'
import { imageFileToSquarePngDataUrl } from '@/utils/image'
import { slugify } from '@/utils/slug'

export function MyGroups() {
  usePageTitle('My Groups')
  const navigate = useNavigate()
  const { refreshAccessToken } = useAuth()
  const { groups, isLoading, isRefetching, error, refetch } = useGroups()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupSlug, setNewGroupSlug] = useState('')
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  const [newGroupIcon, setNewGroupIcon] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isProcessingIcon, setIsProcessingIcon] = useState(false)
  const [iconError, setIconError] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const iconFileInputRef = useRef<HTMLInputElement | null>(null)

  const handleNameChange = (name: string) => {
    setNewGroupName(name)
    // Auto-generate slug unless user has manually edited it
    if (!slugManuallyEdited) {
      setNewGroupSlug(slugify(name))
    }
  }

  const handleSlugChange = (slug: string) => {
    // Normalize as they type (only allow valid slug characters)
    const normalized = slug.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setNewGroupSlug(normalized)
    setSlugManuallyEdited(true)
  }

  const handleCreateGroup = async () => {
    setIsCreating(true)
    setCreateError(null)
    try {
      const created = await groupService.create({
        id: newGroupSlug,
        name: newGroupName,
        ...(newGroupIcon && { icon: newGroupIcon }),
      })
      
      // Refresh app token to include the new group in adminGroupIds
      await refreshAccessToken()
      
      setCreateDialogOpen(false) // This triggers reset via handleDialogOpenChange
      navigate(`/group/${created.id}`)
    } catch (err) {
      const apiErr = err as { status?: number; message?: string }
      if (apiErr?.status === 409) {
        setCreateError('This URL is already taken. Please choose a different one.')
      } else {
        setCreateError(apiErr?.message || 'Failed to create group')
      }
      console.error('Failed to create group:', err)
    } finally {
      setIsCreating(false)
    }
  }

  const handleIconFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsProcessingIcon(true)
    setIconError(null)

    try {
      const dataUrl = await imageFileToSquarePngDataUrl(file, 256)
      setNewGroupIcon(dataUrl)
    } catch (err) {
      setIconError(err instanceof Error ? err.message : 'Failed to process icon image')
    } finally {
      setIsProcessingIcon(false)
      // Allow picking the same file again if needed
      if (iconFileInputRef.current) {
        iconFileInputRef.current.value = ''
      }
    }
  }

  const handleDialogOpenChange = (open: boolean) => {
    setCreateDialogOpen(open)
    if (!open) {
      // Reset form when closing
      setNewGroupName('')
      setNewGroupSlug('')
      setSlugManuallyEdited(false)
      setNewGroupIcon(null)
      setIconError(null)
      setCreateError(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getDaysUntil = (dateStr: string) => {
    const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    return days > 0 ? days : 0
  }

  const getOrdinalSuffix = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return s[(v - 20) % 10] || s[v] || s[0]
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">My Groups</h1>
              {isRefetching && (
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Refreshing…
                </span>
              )}
            </div>
            <p className="text-muted-foreground">Manage your badminton groups and leagues</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create a New Group</DialogTitle>
                <DialogDescription>
                  Start your own badminton group and invite members to compete.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="group-name">Group Name</Label>
                  <Input
                    id="group-name"
                    placeholder="e.g., Downtown Badminton Group"
                    value={newGroupName}
                    onChange={(e) => handleNameChange(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="group-slug">URL</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">badminton-leagues.com/group/</span>
                    <Input
                      id="group-slug"
                      placeholder="my-group"
                      value={newGroupSlug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This will be the unique URL for your group. Only lowercase letters, numbers, and hyphens.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label>Icon</Label>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center text-2xl overflow-hidden border">
                      {newGroupIcon?.startsWith('data:') ? (
                        <img src={newGroupIcon} alt="Selected icon" className="h-full w-full object-cover" />
                      ) : (
                        <Trophy className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                      <Input
                        ref={iconFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleIconFileChange}
                        disabled={isProcessingIcon || isCreating}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setNewGroupIcon(null)}
                          disabled={!newGroupIcon || isProcessingIcon || isCreating}
                        >
                          Remove icon
                        </Button>
                        {isProcessingIcon && (
                          <span className="text-xs text-muted-foreground">Processing…</span>
                        )}
                      </div>
                      {iconError && (
                        <p className="text-xs text-destructive">{iconError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {createError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {createError}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
                  Cancel
                </Button>
                <Button onClick={handleCreateGroup} disabled={!newGroupName.trim() || !newGroupSlug.trim() || isCreating}>
                  {isCreating ? 'Creating...' : 'Create Group'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-12 w-12 rounded-lg" />
                      <div className="space-y-2">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg px-3 py-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-full" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-20 rounded-full" />
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card className="p-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to load groups</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={refetch} variant="outline">
              Try Again
            </Button>
          </Card>
        ) : groups.length === 0 ? (
          <Card className="p-12 text-center">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No groups yet</h3>
            <p className="text-muted-foreground mb-4">
              Join an existing group or create your own to get started.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Group
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {groups.map((group) => (
              <Link key={group.id} to={`/group/${group.id}`}>
              <Card className="group cursor-pointer h-full">
                <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-2xl overflow-hidden">
                        {group.icon?.startsWith('data:') || group.icon?.startsWith('https://') ? (
                          <img src={group.icon} alt={group.name} className="h-full w-full object-cover" />
                        ) : (
                          <Trophy className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">{group.name}</CardTitle>
                          <GroupTypeBadge type={group.type} />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="h-3 w-3" />
                          {group.memberCount} members
                          {group.brackets && group.brackets.length > 0 && (
                            <>
                              <span className="text-muted-foreground/50">•</span>
                              {group.brackets.length} bracket{group.brackets.length !== 1 ? 's' : ''}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Season Info - Compact (only show if there's a season) */}
                  {group.currentSeason ? (
                    <div className="bg-muted/50 rounded-lg px-3 py-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{group.currentSeason}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          group.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {group.status === 'active' ? 'Active' : 'Upcoming'}
                        </span>
                      </div>
                      {group.seasonStart && group.seasonEnd && (
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{formatDate(group.seasonStart)} – {formatDate(group.seasonEnd)}</span>
                          <span>
                            {getDaysUntil(group.seasonEnd) > 0 
                              ? `${getDaysUntil(group.seasonEnd)} days left`
                              : 'Ended'
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-muted/50 rounded-lg px-3 py-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        group.status === 'new'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {group.status === 'new' ? 'New Group' : 'No Season'}
                      </span>
                    </div>
                  )}
                  
                  {/* Footer with role badges and meta */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {group.myBracket && (
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary flex items-center gap-1">
                          <Medal className="h-3 w-3" />
                          {group.myBracket}
                        </span>
                      )}
                      {group.isAdmin && (
                        <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          Admin
                        </span>
                      )}
                      {!group.myBracket && !group.isAdmin && (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                          Spectator
                        </span>
                      )}
                    </div>
                    {group.currentSeason && (
                      <span className="text-xs text-muted-foreground">
                        {(group.pastSeasons ?? 0) === 0 
                          ? '1st season' 
                          : `${(group.pastSeasons ?? 0) + 1}${getOrdinalSuffix((group.pastSeasons ?? 0) + 1)} season`
                        }
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
